import {
	RIGHTS_INVENTORY_DEFAULT_GROUPING,
	RIGHTS_PAGE_DEFAULT_INVENTORY_VIEW,
	RIGHTS_PAGE_DEFAULT_RIGHTS,
} from '@entities/rights/constants';
import {
	Channel,
	ContentCode,
	Genre,
	GroupingMode,
	Inventory,
	InventoryFilters,
	Language,
	MediaType,
	ProductCode,
	ProjectCode,
	RightsInventoryBuffer,
	RightsLanguageMetadata,
	RightsSourcesOption,
	RightsView,
	Territory,
	View,
	WorkflowStatus,
} from '@entities/rights/rights.interfaces';
import {createReducer, on} from '@ngrx/store';
import {rightsActions} from '@store/rights/actions';
import {LoadingStatus, nameOfFactory, status} from 'amc-common-components';
import {assoc, pipe} from 'ramda';

/**
 * Auxiliary store state interface declaration.
 */
export interface RightsState {
	/** {Inventory[]} inventory */
	inventory: Inventory[];
	/** {InventoryFilters} inventoryFilters */
	inventoryFilters: InventoryFilters;
	/** {GroupingMode} inventoryGrouping */
	inventoryGrouping: GroupingMode;
	/** {LoadingStatus} inventoryLoadingStatus */
	inventoryLoadingStatus: LoadingStatus;
	/** {boolean} inventoryNotFound */
	inventoryNotFound: boolean;
	/** {RightsInventoryBuffer} inventoryPinned */
	inventoryPinned: RightsInventoryBuffer;
	/** {View} inventoryView */
	inventoryView: View;
	/** {Inventory[]} inventoryXLS */
	inventoryXLS: Inventory[];
	/** {LoadingStatus} inventoryXLSLoadingStatus */
	inventoryXLSLoadingStatus: LoadingStatus;
	/** {string[]} inventoryXLSSelection */
	inventoryXLSSelection: string[];
	/** {Language[]} languages */
	languages: Language[];
	/** {LoadingStatus} languagesLoadingStatus */
	languagesLoadingStatus: LoadingStatus;
	/** {Channel[]} channels */
	channels: Channel[];
	/** {LoadingStatus} channelsLoadingStatus */
	channelsLoadingStatus: LoadingStatus;
	/** {Channel[]} genres */
	genres: Genre[];
	/** {LoadingStatus} genresLoadingStatus */
	genresLoadingStatus: LoadingStatus;
	/** {Record<string, RightsLanguageMetadata[]>} languagesMetadata */
	languagesMetadata: Record<string, RightsLanguageMetadata[]>;
	/** {LoadingStatus} languagesMetadataLoadingStatus */
	languagesMetadataLoadingStatus: LoadingStatus;
	/** {MediaType[]} mediaTypes */
	mediaTypes: MediaType[];
	/** {LoadingStatus} mediaTypesLoadingStatus */
	mediaTypesLoadingStatus: LoadingStatus;
	/** {RightsSourcesOption[]} rightsSources */
	rightsSources: RightsSourcesOption[];
	/** {LoadingStatus} rightsSourcesLoadingStatus */
	rightsSourcesLoadingStatus: LoadingStatus;
	/** {RightsView} rightsView */
	rightsView: RightsView;
	/** {number} pagesCount */
	pagesCount: number;
	/** {LoadingStatus} pagesCountLoadingStatus */
	pagesCountLoadingStatus: LoadingStatus;
	/** {number} selectedPage */
	selectedPage: number;
	/** {RightsSourcesOption} selectedSource */
	selectedSource: RightsSourcesOption;
	/** {Territory[]} territories */
	territories: Territory[];
	/** {LoadingStatus} territoriesLoadingStatus */
	territoriesLoadingStatus: LoadingStatus;
	/** {ContentCode[]} brands */
	brands: ContentCode[];
	/** {LoadingStatus} brandsLoadingStatus */
	brandsLoadingStatus: LoadingStatus;
	/** {ProjectCode[]} projectTypes */
	projectTypes: ProjectCode[];
	/** {LoadingStatus} projectTypesLoadingStatus */
	projectTypesLoadingStatus: LoadingStatus;
	/** {ProductCode[]} productOwners */
	productOwners: ProductCode[];
	/** {LoadingStatus} productOwnersLoadingStatus */
	productOwnersLoadingStatus: LoadingStatus;
	/** {WorkflowStatus[]} workflowStatuses */
	workflowStatuses: WorkflowStatus[];
	/** {LoadingStatus} workflowStatusesLoadingStatus */
	workflowStatusesLoadingStatus: LoadingStatus;
}

/** Auxiliary 'initialState' variable.' */
const initialState: RightsState = {
	inventory: null,
	inventoryFilters: null,
	inventoryGrouping: RIGHTS_INVENTORY_DEFAULT_GROUPING,
	inventoryLoadingStatus: status.default,
	inventoryNotFound: false,
	inventoryPinned: new Map<string, Inventory[]>(),
	inventoryView: RIGHTS_PAGE_DEFAULT_INVENTORY_VIEW,
	inventoryXLS: null,
	inventoryXLSLoadingStatus: status.default,
	inventoryXLSSelection: [],
	languages: null,
	languagesLoadingStatus: status.default,
	channels: null,
	channelsLoadingStatus: status.default,
	genres: null,
	genresLoadingStatus: status.default,
	languagesMetadata: null,
	languagesMetadataLoadingStatus: status.default,
	mediaTypes: null,
	mediaTypesLoadingStatus: status.default,
	rightsSources: null,
	rightsSourcesLoadingStatus: status.default,
	rightsView: RIGHTS_PAGE_DEFAULT_RIGHTS.id,
	pagesCount: 1,
	pagesCountLoadingStatus: status.loading,
	selectedPage: 1,
	selectedSource: null,
	territories: null,
	territoriesLoadingStatus: status.default,
	brands: null,
	brandsLoadingStatus: status.default,
	projectTypes: null,
	projectTypesLoadingStatus: status.default,
	productOwners: null,
	productOwnersLoadingStatus: status.default,
	workflowStatuses: null,
	workflowStatusesLoadingStatus: status.default,
};
/** Auxiliary 'prop' variable.' */
const prop = nameOfFactory<RightsState>();

/** Auxiliary 'rightsReducer' variable.' */
export const rightsReducer = createReducer(
	initialState,
	/* Reset State */
	on(rightsActions.resetState, () => ({...initialState})),

	/* Rights Sources */
	on(rightsActions.rightsSources.requested, assoc(prop('rightsSourcesLoadingStatus'), status.loading)),
	on(rightsActions.rightsSources.succeeded, (state, {sources}) =>
		pipe(
			assoc(prop('rightsSourcesLoadingStatus'), status.loaded),
			assoc(prop('rightsSources'), sources || []),
			assoc(prop('selectedSource'), sources && sources[0])
		)(state)
	),
	on(rightsActions.rightsSources.failed, (state, {error}) =>
		assoc(prop('rightsSourcesLoadingStatus'), status.error(error), state)
	),
	on(rightsActions.source, (state, {source}) => assoc(prop('selectedSource'), source, state)),

	/* Inventory */
	on(rightsActions.getInventory.requested, assoc(prop('inventoryLoadingStatus'), status.loading)),
	on(rightsActions.getInventory.succeeded, (state, {inventory}) =>
		pipe(
			assoc(prop('inventoryNotFound'), inventory !== null && !inventory.length),
			assoc(prop('inventory'), inventory || []),
			assoc(prop('inventoryLoadingStatus'), status.loaded)
		)(state)
	),
	on(rightsActions.getInventory.failed, (state, {error}) =>
		assoc(prop('inventoryLoadingStatus'), status.error(error), state)
	),
	on(rightsActions.cancelInventoryLoading, assoc(prop('inventoryLoadingStatus'), status.loaded)),
	on(rightsActions.setInventoryGrouping, (state, {grouping}) => assoc(prop('inventoryGrouping'), grouping, state)),
	on(rightsActions.setInventoryFilters, (state, {filters}) => assoc(prop('inventoryFilters'), filters, state)),
	on(rightsActions.setInventoryView, (state, {view}) => assoc(prop('inventoryView'), view, state)),

	/* Inventory BUFFER */
	on(rightsActions.copyInventoryToBuffer.succeeded, (state, {buffer}) =>
		pipe(assoc(prop('inventoryPinned'), buffer), assoc(prop('inventory'), []))(state)
	),
	on(rightsActions.removeInventoryFromBuffer.succeeded, (state, {buffer}) =>
		pipe(assoc(prop('inventoryPinned'), buffer))(state)
	),
	on(rightsActions.modifyInventoryInBuffer.succeeded, (state, {buffer}) =>
		pipe(assoc(prop('inventoryPinned'), buffer))(state)
	),

	/* Inventory XLS */
	on(rightsActions.inventoryXLS.requested, assoc(prop('inventoryXLSLoadingStatus'), status.loading)),
	on(rightsActions.inventoryXLS.succeeded, (state) =>
		pipe(assoc(prop('inventoryXLSLoadingStatus'), status.loaded))(state)
	),
	on(rightsActions.inventoryXLS.failed, (state, {error}) =>
		assoc(prop('inventoryXLSLoadingStatus'), status.error(error), state)
	),

	/* Inventory XLS Selection */
	on(rightsActions.inventoryXLSSelection.requested, (state, {key}) =>
		pipe(
			assoc(
				prop('inventoryXLSSelection'),
				state.inventoryXLSSelection.includes(key)
					? state.inventoryXLSSelection.filter((val: string) => val !== key)
					: [...new Set([...state.inventoryXLSSelection, key])]
			)
		)(state)
	),
	on(rightsActions.removeInventoryXLSSelection.requested, (state, {key}) =>
		pipe(
			assoc(
				prop('inventoryXLSSelection'),
				state.inventoryXLSSelection.filter((val: string) => val !== key)
			)
		)(state)
	),

	/* MediaTypes */
	on(rightsActions.getMediaTypes.requested, assoc(prop('mediaTypesLoadingStatus'), status.loading)),
	on(rightsActions.getMediaTypes.succeeded, (state, {mediaTypes}) =>
		pipe(assoc(prop('mediaTypesLoadingStatus'), status.loaded), assoc(prop('mediaTypes'), mediaTypes || []))(state)
	),
	on(rightsActions.getMediaTypes.failed, (state, {error}) =>
		assoc(prop('mediaTypesLoadingStatus'), status.error(error), state)
	),

	/* Territories */
	on(rightsActions.getTerritories.requested, assoc(prop('territoriesLoadingStatus'), status.loading)),
	on(rightsActions.getTerritories.succeeded, (state, {territories}) =>
		pipe(
			assoc(prop('territoriesLoadingStatus'), status.loaded),
			assoc(prop('territories'), territories || [])
		)(state)
	),
	on(rightsActions.getTerritories.failed, (state, {error}) =>
		assoc(prop('territoriesLoadingStatus'), status.error(error), state)
	),

	/* Languages */
	on(rightsActions.getLanguages.requested, assoc(prop('languagesLoadingStatus'), status.loading)),
	on(rightsActions.getLanguages.succeeded, (state, {languages}) =>
		pipe(assoc(prop('languagesLoadingStatus'), status.loaded), assoc(prop('languages'), languages || []))(state)
	),
	on(rightsActions.getLanguages.failed, (state, {error}) =>
		assoc(prop('languagesLoadingStatus'), status.error(error), state)
	),

	/* Channels */
	on(rightsActions.getChannels.requested, assoc(prop('channelsLoadingStatus'), status.loading)),
	on(rightsActions.getChannels.succeeded, (state, {channels}) =>
		pipe(assoc(prop('channelsLoadingStatus'), status.loaded), assoc(prop('channels'), channels || []))(state)
	),
	on(rightsActions.getChannels.failed, (state, {error}) =>
		assoc(prop('channelsLoadingStatus'), status.error(error), state)
	),

	/* Genres */
	on(rightsActions.getGenres.requested, assoc(prop('genresLoadingStatus'), status.loading)),
	on(rightsActions.getGenres.succeeded, (state, {genres}) =>
		pipe(assoc(prop('genresLoadingStatus'), status.loaded), assoc(prop('genres'), genres || []))(state)
	),
	on(rightsActions.getGenres.failed, (state, {error}) =>
		assoc(prop('genresLoadingStatus'), status.error(error), state)
	),

	/* Languages Metadata*/
	on(rightsActions.getLanguagesMetadata.requested, assoc(prop('languagesMetadataLoadingStatus'), status.loading)),
	on(rightsActions.getLanguagesMetadata.succeeded, (state, {data}) =>
		pipe(
			assoc(prop('languagesMetadataLoadingStatus'), status.loaded),
			assoc(prop('languagesMetadata'), data || [])
		)(state)
	),
	on(rightsActions.getLanguagesMetadata.failed, (state, {error}) =>
		assoc(prop('languagesMetadataLoadingStatus'), status.error(error), state)
	),

	/* Brands */
	on(rightsActions.getBrands.requested, assoc(prop('brandsLoadingStatus'), status.loading)),
	on(rightsActions.getBrands.succeeded, (state, {brands}) =>
		pipe(assoc(prop('brandsLoadingStatus'), status.loaded), assoc(prop('brands'), brands || []))(state)
	),
	on(rightsActions.getBrands.failed, (state, {error}) =>
		assoc(prop('brandsLoadingStatus'), status.error(error), state)
	),

	/* Project Types */
	on(rightsActions.getProjectTypes.requested, assoc(prop('projectTypesLoadingStatus'), status.loading)),
	on(rightsActions.getProjectTypes.succeeded, (state, {projectTypes}) =>
		pipe(
			assoc(prop('projectTypesLoadingStatus'), status.loaded),
			assoc(prop('projectTypes'), projectTypes || [])
		)(state)
	),
	on(rightsActions.getProjectTypes.failed, (state, {error}) =>
		assoc(prop('projectTypesLoadingStatus'), status.error(error), state)
	),

	/* Product Owners */
	on(rightsActions.getProductOwners.requested, assoc(prop('productOwnersLoadingStatus'), status.loading)),
	on(rightsActions.getProductOwners.succeeded, (state, {productOwners}) =>
		pipe(
			assoc(prop('productOwnersLoadingStatus'), status.loaded),
			assoc(prop('productOwners'), productOwners || [])
		)(state)
	),
	on(rightsActions.getProductOwners.failed, (state, {error}) =>
		assoc(prop('productOwnersLoadingStatus'), status.error(error), state)
	),

	/* Workflow Statuses */
	on(rightsActions.getWorkflowStatuses.requested, assoc(prop('workflowStatusesLoadingStatus'), status.loading)),
	on(rightsActions.getWorkflowStatuses.succeeded, (state, {workflowStatuses}) =>
		pipe(
			assoc(prop('workflowStatusesLoadingStatus'), status.loaded),
			assoc(prop('workflowStatuses'), workflowStatuses || [])
		)(state)
	),
	on(rightsActions.getWorkflowStatuses.failed, (state, {error}) =>
		assoc(prop('workflowStatusesLoadingStatus'), status.error(error), state)
	),

	/* Rights View */
	on(rightsActions.setRightsView, (state, {rights}) => assoc(prop('rightsView'), rights, state)),

	/* Pagination */
	on(rightsActions.getPagesCount.requested, assoc(prop('pagesCountLoadingStatus'), status.loading)),
	on(rightsActions.getPagesCount.succeeded, (state, {pagesCount}) =>
		pipe(assoc(prop('pagesCountLoadingStatus'), status.loaded), assoc(prop('pagesCount'), pagesCount))(state)
	),
	on(rightsActions.getPagesCount.failed, (state, {error}) =>
		assoc(prop('pagesCountLoadingStatus'), status.error(error), state)
	),
	on(rightsActions.selectPage, (state, {page}) => assoc(prop('selectedPage'), page, state))
);
